import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { PageHeader, Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { EmptyState } from '../../components/ui/EmptyState'
import { PermissionGate } from '../../components/PermissionGate'
import { projectsApi } from '../../api/projects.api'
import type { Project, ProjectStatus } from '../../api/types'
import { ProjectFormModal } from './ProjectFormModal'

const STATUS_LABELS: Record<ProjectStatus, { label: string; variant: 'neutral' | 'info' | 'success' | 'warning' }> = {
  PLANNING: { label: 'Planung', variant: 'neutral' },
  ACTIVE: { label: 'Aktiv', variant: 'success' },
  PAUSED: { label: 'Pausiert', variant: 'warning' },
  COMPLETED: { label: 'Abgeschlossen', variant: 'info' },
  ARCHIVED: { label: 'Archiviert', variant: 'neutral' },
}

export function ProjectsPage() {
  const navigate = useNavigate()
  const [formProject, setFormProject] = useState<Project | null | undefined>(undefined)

  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: async () => (await projectsApi.list({ pageSize: 100 })).data })

  return (
    <div>
      <PageHeader
        title="Projekte"
        subtitle="Alle Entwicklungsprojekte im Überblick."
        actions={
          <PermissionGate anyOf={['projects.create']}>
            <Button variant="primary" onClick={() => setFormProject(null)}>
              + Neues Projekt
            </Button>
          </PermissionGate>
        }
      />

      {projectsQuery.data?.length === 0 ? (
        <EmptyState mascot title="Noch keine Projekte" description="Lege dein erstes Projekt an, um mit Aufgaben zu starten." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-4)' }}>
          {projectsQuery.data?.map((project) => {
            const statusInfo = STATUS_LABELS[project.status]
            return (
              <Card key={project.id} style={{ cursor: 'pointer' }}>
                <div onClick={() => navigate(`/projects/${project.id}`)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: project.color ?? 'var(--accent-9)' }} />
                    <div style={{ fontWeight: 600 }}>{project.name}</div>
                  </div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', minHeight: 36 }}>
                    {project.description || 'Keine Beschreibung.'}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                    <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{project._count.tasks} Aufgaben</span>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {formProject !== undefined ? <ProjectFormModal project={formProject} onClose={() => setFormProject(undefined)} /> : null}
    </div>
  )
}
